import React, { Fragment } from 'react';
import { toast } from 'react-toastify';

import { Col } from 'antd';

import axios from 'axios';
import { Field } from 'formik';
import type { IForm } from 'v2/components/Shared/Content';
import { SelectFormField } from 'v2/components/Shared/FormFields/SelectFormField';
import { TextFormField } from 'v2/components/Shared/FormFields/TextFormField';
import type { IAddressForm } from 'v2/interfaces/address';

interface IBrState {
  label: string;
  value: string;
  sigla: string;
}

interface IResult {
  bairro: string;
  cep: string;
  complemento: string;
  gia: string;
  ibge: string;
  localidade: string;
  logradouro: string;
  uf: string;
  unidade: string;
  erro?: boolean;
}

type IProps = IForm<IAddressForm>;

const Address = (props: IProps) => {
  const { setFocus, setFieldValue, values } = props;

  const brStates: IBrState[] = [
    { label: 'Acre', value: 'Acre', sigla: 'AC' },
    { label: 'Alagoas', value: 'Alagoas', sigla: 'AL' },
    { label: 'Amapá', value: 'Amapá', sigla: 'AP' },
    { label: 'Amazonas', value: 'Amazonas', sigla: 'AM' },
    { label: 'Bahia', value: 'Bahia', sigla: 'BA' },
    { label: 'Ceará', value: 'Ceará', sigla: 'CE' },
    { label: 'Distrito Federal', value: 'Distrito Federal', sigla: 'DF' },
    { label: 'Espírito Santo', value: 'Espírito Santo', sigla: 'ES' },
    { label: 'Goiás', value: 'Goiás', sigla: 'GO' },
    { label: 'Maranhão', value: 'Maranhão', sigla: 'MA' },
    { label: 'Mato Grosso', value: 'Mato Grosso', sigla: 'MT' },
    { label: 'Mato Grosso do Sul', value: 'Mato Grosso do Sul', sigla: 'MS' },
    { label: 'Minas Gerais', value: 'Minas Gerais', sigla: 'MG' },
    { label: 'Pará', value: 'Pará', sigla: 'PA' },
    { label: 'Paraíba', value: 'Paraíba', sigla: 'PB' },
    { label: 'Paraná', value: 'Paraná', sigla: 'PR' },
    { label: 'Pernambuco', value: 'Pernambuco', sigla: 'PE' },
    { label: 'Piauí', value: 'Piauí', sigla: 'PI' },
    { label: 'Rio de Janeiro', value: 'Rio de Janeiro', sigla: 'RJ' },
    { label: 'Rio Grande do Norte', value: 'Rio Grande do Norte', sigla: 'RN' },
    { label: 'Rio Grande do Sul', value: 'Rio Grande do Sul', sigla: 'RS' },
    { label: 'Rondônia', value: 'Rondônia', sigla: 'RO' },
    { label: 'Roraima', value: 'Roraima', sigla: 'RR' },
    { label: 'Santa Catarina', value: 'Santa Catarina', sigla: 'SC' },
    { label: 'São Paulo', value: 'São Paulo', sigla: 'SP' },
    { label: 'Sergipe', value: 'Sergipe', sigla: 'SE' },
    { label: 'Tocantins', value: 'Tocantins', sigla: 'TO' }
  ];

  const getCepData = async (cep: string) => {
    const result = await axios({
      method: 'GET',
      baseURL: `https://viacep.com.br/ws/${cep}/json`,
      headers: {
        'Content-type': 'application/json'
      }
    });

    const data: IResult = result.data;
    return data;
  };

  const onBlur = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const cep = e.target.value;

    if (cep) {
      if (!cep.match(/^[0-9]{8}$/)) {
        return;
      }

      try {
        const data: IResult = await getCepData(cep);
        const state: IBrState = brStates.find((item: IBrState) => item.sigla === data.uf);

        setFieldValue('address_state', state?.value ?? '');
        setFieldValue('address_number', '');
        setFieldValue('address_complement', '');
        setFieldValue('address_city_id', 0);
        setFieldValue('address_country', 'Brasil');

        if (data.erro) {
          setFieldValue('address_neighborhood', '');
          setFieldValue('address_street', '');
          setFieldValue('address_city_name', '');
          setFieldValue('address_city_ibge', '');
          return;
        }

        setFieldValue('address_neighborhood', data.bairro);
        setFieldValue('address_street', data.logradouro);
        setFieldValue('address_city_name', data.localidade);
        setFieldValue('address_city_ibge', data.ibge);
      } catch (e) {
        toast.error('Ocorreu um erro ao tentar pesquisar cep');
      }
    }
  };

  const setCityByCep = async () => {
    let cep = props.values.address_zip_code;
    cep = cep ? cep.replace('-', '') : '';

    if (cep) {
      const data: IResult = await getCepData(cep);
      const state: IBrState = brStates.find((item: IBrState) => item.sigla === data.uf);

      setFieldValue('address_state', state?.value ?? '');
      setFieldValue('address_city_id', 0);
      setFieldValue('address_country', 'Brasil');

      if (data.erro) {
        return;
      }

      setFieldValue('address_city_name', data.localidade);
      setFieldValue('address_city_ibge', data.ibge);
    }
  };

  return (
    <Fragment>
      <Col xs={24} sm={12}>
        <Field
          label='CEP'
          name='address_zip_code'
          id='address_zip_code'
          mask='zipcode'
          onFocus={setFocus}
          onBlur={onBlur}
          component={TextFormField}
          value={values.address_zip_code ?? ''}
        />
      </Col>
      <Col xs={24} sm={12}>
        <Field
          label='Endereço'
          name='address_street'
          id='address_street'
          onFocus={setFocus}
          component={TextFormField}
        />
      </Col>
      <Col xs={24} sm={6}>
        <Field label='Número' name='address_number' id='address_number' onFocus={setFocus} component={TextFormField} />
      </Col>
      <Col xs={24} sm={6}>
        <Field
          label='Complemento'
          name='address_complement'
          id='address_complement'
          onFocus={setFocus}
          component={TextFormField}
        />
      </Col>
      <Col xs={24} sm={8}>
        <Field
          label='Bairro'
          name='address_neighborhood'
          id='address_neighborhood'
          onFocus={setFocus}
          component={TextFormField}
        />
      </Col>
      <Col xs={24} sm={8}>
        <Field
          label='Cidade'
          name='address_city_name'
          id='address_city_name'
          onBlur={setCityByCep}
          onFocus={setCityByCep}
          component={TextFormField}
        />
      </Col>
      <Col xs={24} sm={8}>
        <Field
          label='Estado'
          name='address_state'
          id='address_state'
          onFocus={setFocus}
          options={brStates}
          component={SelectFormField}
        />
      </Col>
    </Fragment>
  );
};

export default Address;
